import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import apiSubscription from "../../services/subscription";
import { tokenizeCard } from "./useOmiseToken";

/**
 * The atomic submit pipeline. Runs in 2 phases visible to the user:
 *
 *  1. tokenizing  — Omise turns raw card data into a token
 *  2. persisting  — backend does authorize → DB transaction → capture
 *                   (this is ONE HTTP call from the frontend's perspective)
 *
 * The PaymentProgressModal subscribes to `phase` to show what's happening.
 *
 * Mock mode:
 *  When USE_MOCK_DATA = true, the persisting step returns a fake response
 *  after a short delay so SuccessModal can be tested end-to-end without a
 *  real backend. Set to false (or env-driven) when wiring up to the API.
 */

// const USE_MOCK_DATA = true;
const USE_MOCK_DATA = false;

export function useSubmitSubscription() {
  const [phase, setPhase] = useState("idle");

  const mutation = useMutation({
    mutationFn: async (args) => {
      const {
        id_project,
        id_subscription_plan_table,
        omise_public_key,
        company,
        account,
        payment,
        promo,
        referral,
        // Phase 2 — present when the user signed in via the modal in
        // AccountStep. Carries the accessToken from /auth_user, which
        // we forward to the backend as the x-has-accesstoken header
        // so the request lands in "existing user" mode (re-uses
        // Bill_User, only adds a new BillAndBranch row).
        //
        // null/undefined for normal new-user signups — backend treats
        // those as the standard greenfield path.
        bearerToken,
      } = args;

      // ─── Phase 1: tokenize card ───────────────────────────
      setPhase("tokenizing");
      const omise_token = await tokenizeCard(payment, omise_public_key);

      // ─── Phase 2: atomic backend call ─────────────────────
      setPhase("persisting");

      const payload = {
        id_project,
        id_subscription_plan_table,
        omise_token,
        company,
        account: stripPasswordConfirm(account),
        promo: promo ? { id_promo_code: promo.id_promo_code } : null,
        referral: referral ? { id_referral_key: referral.id_referral_key } : null,
      };

      let result;
      if (USE_MOCK_DATA) {
        // Simulate backend authorize → DB transaction → capture (1.5s)
        console.log("payload:", payload);
        console.log("📤 PAYLOAD raw:", payload);
        console.log("📤 email field:", payload.account.email);
        console.log("📤 email length:", payload.account.email.length);
        console.log("📤 email startsWith [:", payload.account.email.startsWith("["));
        console.log("📤 bearerToken present:", !!bearerToken);

        await new Promise((r) => setTimeout(r, 1500));
        result = {
          status: "success",
          id_company: 999,
          project_code: "LEV-999999",
          id_bill: 12345,
        };
      } else {
        // Pass bearerToken via the auth-options arg. createSubscription
        // forwards it through Provider-Api as `x-has-accesstoken`.
        // When omitted the call is unauthenticated (new-user path).
        result = await apiSubscription.createSubscription(payload, {
          token: bearerToken || undefined,
        });
      }

      setPhase("done");
      return result;
    },
    onError: () => {
      setPhase("idle");
    },
  });

  const submit = useCallback(
    (args) => mutation.mutateAsync(args),
    [mutation]
  );

  const reset = useCallback(() => {
    setPhase("idle");
    mutation.reset();
  }, [mutation]);

  return {
    submit,
    reset,
    phase,
    isPending: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
  };
}

// Don't send confirm_password to backend — it's UI-only.
function stripPasswordConfirm(account) {
  if (!account) return account;
  const { confirm_password, ...rest } = account;
  return rest;
}